import React, { useLayoutEffect, useState } from 'react';
import cx from 'classnames';
import { useTranslations } from '@unisporkal/localization';
import { useScroll } from '@unisporkal/utilities';
import { Button, ButtonVariantEnum } from '@unisporkal/alliance-ui';
import DownArrowIcon from '../../../icons/DownArrow.svg?icon';
import acquisitionActions from '../../../support/acquisitionActions';
import acquisitionTypes from '../../../support/acquisitionTypes';
import assetSizeLabel from '../../../support/assetSizeLabel';
import formatPrice from '../../../support/formatPrice';
import styles from './MobileStickyButton.module.scss';
import { useAcquisitionType } from '../../../hooks/stateful/useAcquisitionType';
import useSelectedIndex from '../../../hooks/stateful/useSelectedIndex';
import { useUltrapackLicense } from '../../../hooks/useUltraPackLicense';

const MobileStickyButton = ({ action, children }) => {
  const t = useTranslations();
  const { selectedOption, selectedSize } = useSelectedIndex();
  const { acquisitionType } = useAcquisitionType();
  const { isUltraPackLicense, formattedTotalPrice: ultraPackPrice } =
    useUltrapackLicense({ priceDecimalDigits: 0 });

  const [scrollThreshold, setScrollThreshold] = useState(0);
  const scroll = useScroll({
    threshold: scrollThreshold,
    scrollAfterThreshold: false,
  });

  useLayoutEffect(() => {
    const buttonContainer = document.querySelector(
      "[data-testid='acquisition-buttons-container']"
    );
    const buttonsRect = buttonContainer?.getBoundingClientRect();
    if (buttonsRect) {
      setScrollThreshold(
        Math.abs(buttonsRect.bottom + window.scrollY - window.innerHeight)
      );
    }
  }, []);

  const shouldHideStickyContent = () => {
    const customLicense = selectedOption?.type === 'customlicense';
    return (
      customLicense || action === acquisitionActions.CONTACT_US_MARKET_FREEZE
    );
  };

  const getUltraPackLicenseContent = () =>
    `${t('5_large_images_or_videos')} ${ultraPackPrice.formattedPrice}`;

  const getDefaultContent = () =>
    selectedSize ? `${assetSizeLabel(t, selectedSize)}` : null;

  const isLicenseTypeWithPrice = () =>
    acquisitionType === acquisitionTypes.LICENSE &&
    (selectedSize.discountPrice || selectedSize.price);

  const getLicenseTypeContent = () => {
    if (shouldHideStickyContent()) return null;
    return `${assetSizeLabel(t, selectedSize)} ${formatPrice(
      selectedSize.discountPrice || selectedSize.price
    )}`;
  };
  const getMobileStickyButtonContent = () => {
    if (isLicenseTypeWithPrice()) {
      return getLicenseTypeContent();
    }

    if (isUltraPackLicense) {
      return getUltraPackLicenseContent();
    }

    return getDefaultContent();
  };

  const scrollToButtons = () => {
    window.scrollTo(0, scrollThreshold + 1);
  };

  return (
    <div
      className={styles.stickyContainer}
      data-testid="mobile-sticky-container"
    >
      {scroll.isNone() ? (
        children
      ) : (
        <div
          className={cx({ [styles.stickyButton]: !scroll.isNone() })}
          data-testid="mobile-sticky-btn"
        >
          <div className={styles.stickyContent}>
            <Button
              onClick={scrollToButtons}
              variant={ButtonVariantEnum.TEXT}
            >
              {getMobileStickyButtonContent()}
              <div className={styles.stickyArrowIcon}>
                <DownArrowIcon />
              </div>
            </Button>
          </div>
          {children}
        </div>
      )}
    </div>
  );
};

export default MobileStickyButton;
